// client/src/components/world/StructureNavigator.jsx
// Итерация 6.2: список палуб структуры (station / ship / shipyard / spaceport).
// Работает с structureInfo.available_decks + structureInfo.installed_modules.

import React, { useMemo } from 'react';
import {
  Box, Typography, List, ListItem, ListItemText, ListItemButton,
  Chip, Stack, CircularProgress, Alert, Divider,
} from '@mui/material';
import {
  Layers as LayersIcon,
  ViewModule as ModuleIcon,
  CheckCircle as ActiveIcon,
} from '@mui/icons-material';

export default function StructureNavigator({
  locationInfo,
  structureInfo,
  loading,
  error,
  currentDeckId,
  onDeckSelect,
}) {
  const decks = useMemo(() => {
    const raw = structureInfo?.available_decks;
    if (!Array.isArray(raw)) return [];
    return [...raw].sort((a, b) => (a.level ?? 0) - (b.level ?? 0));
  }, [structureInfo]);

  const totalModules = useMemo(() => {
    const modules = structureInfo?.installed_modules;
    if (Array.isArray(modules)) return modules.length;
    return decks.reduce((sum, d) => sum + (d.module_count || 0), 0);
  }, [structureInfo, decks]);

  const headerName = locationInfo?.name || structureInfo?.name || 'Структура';

  return (
    <Box
      sx={{
        p: 1,
        borderRadius: 1.5,
        bgcolor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.05)',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <LayersIcon sx={{ color: '#00e5ff', fontSize: 14 }} />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: 'text.secondary',
            fontSize: '0.6rem',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          🏗 {headerName}
        </Typography>
        <Chip
          label={`${decks.length} палуб`}
          size="small"
          sx={{
            height: 14,
            '& .MuiChip-label': { fontSize: '0.4rem', px: 0.5 },
            bgcolor: 'rgba(0,229,255,0.1)',
            color: '#00e5ff',
          }}
        />
      </Box>

      {/* Loading / Error / Empty */}
      {loading && !structureInfo && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.55rem' }}>
            Загрузка палуб...
          </Typography>
        </Box>
      )}

      {error && !structureInfo && (
        <Alert severity="info" sx={{ py: 0, fontSize: '0.55rem' }}>
          Палубы недоступны
        </Alert>
      )}

      {!loading && !error && decks.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.55rem', py: 1, display: 'block' }}>
          Палубы не обнаружены
        </Typography>
      )}

      {/* Список палуб */}
      {decks.length > 0 && (
        <List dense disablePadding sx={{ maxHeight: 220, overflowY: 'auto' }}>
          {decks.map((deck) => {
            const deckId = deck.id || `deck-${deck.level}`;
            const isCurrent = deckId === currentDeckId;
            return (
              <ListItemButton
                key={deckId}
                selected={isCurrent}
                onClick={() => onDeckSelect?.(deckId, deck)}
                sx={{
                  py: 0.25,
                  px: 0.5,
                  borderRadius: 0.75,
                  minHeight: 26,
                  '&.Mui-selected': {
                    bgcolor: 'rgba(0,229,255,0.12)',
                    borderLeft: '2px solid #00e5ff',
                    '&:hover': { bgcolor: 'rgba(0,229,255,0.18)' },
                  },
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
                }}
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.55rem',
                          fontWeight: isCurrent ? 700 : 500,
                          color: isCurrent ? '#00e5ff' : 'text.primary',
                        }}
                      >
                        {deck.name || `Палуба ${deck.level}`}
                      </Typography>
                      {isCurrent && <ActiveIcon sx={{ fontSize: 10, color: '#00e5ff' }} />}
                    </Stack>
                  }
                  secondary={
                    <Typography variant="caption" sx={{ fontSize: '0.4rem', color: 'text.secondary' }}>
                      {deck.module_count ?? 0} модулей
                    </Typography>
                  }
                  primaryTypographyProps={{ component: 'div' }}
                  secondaryTypographyProps={{ component: 'div' }}
                />
              </ListItemButton>
            );
          })}
        </List>
      )}

      {/* Footer: total modules */}
      {totalModules > 0 && (
        <>
          <Divider sx={{ my: 0.5, opacity: 0.3 }} />
          <Stack direction="row" spacing={0.5} alignItems="center">
            <ModuleIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
            <Typography variant="caption" sx={{ fontSize: '0.5rem', color: 'text.secondary' }}>
              Модулей: <b>{totalModules}</b>
            </Typography>
          </Stack>
        </>
      )}
    </Box>
  );
}
